import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  Alert,
  Avatar,
  Row,
  Col,
  Flex,
  Spin,
} from 'antd';
import {
  MailOutlined,
  LockOutlined,
  BankOutlined,
  FileProtectOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import authService from '../../services/authService';
import PoweredByVarexe from '../../components/common/PoweredByVarexe';

const { Title, Text, Paragraph } = Typography;

const LoginPage = () => {
  const { organizationPublicId } = useParams();
  const isOrgScopedRoute = Boolean(organizationPublicId);

  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orgName, setOrgName] = useState(null);
  const [orgInfoLoading, setOrgInfoLoading] = useState(isOrgScopedRoute);
  const [orgInfoError, setOrgInfoError] = useState(null);

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard', { replace: true });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!isOrgScopedRoute) return undefined;
    let cancelled = false;
    (async () => {
      setOrgInfoLoading(true);
      setOrgInfoError(null);
      try {
        const data = await authService.getOrganizationBasicInfo(organizationPublicId);
        if (!cancelled) setOrgName(data?.name ?? null);
      } catch {
        if (!cancelled) {
          setOrgInfoError(
            'This sign-in link is invalid or the organization could not be found.',
          );
          setOrgName(null);
        }
      } finally {
        if (!cancelled) setOrgInfoLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOrgScopedRoute, organizationPublicId]);

  const handleSubmit = async (values) => {
    const orgId = isOrgScopedRoute
      ? organizationPublicId
      : String(values.organization_id ?? '').trim();
    setLoading(true);
    setError(null);
    try {
      await login(values.email, values.password, orgId);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.message
        ?? err?.response?.data?.error
        ?? 'Login failed. Please check your credentials.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const heroTitle = orgName || 'Customer Portal';
  const heroSubtitle = isOrgScopedRoute
    ? "You're signing in to this organization's secure portal."
    : 'Sign in to access your invoices and estimates';

  const cardIntro = isOrgScopedRoute
    ? 'Enter your portal email and password to continue.'
    : 'Enter your organization ID and credentials below';

  return (
    <Flex
      justify="center"
      align="center"
      style={{
        minHeight: '100vh',
        padding: '24px 16px',
      }}
    >
      <Row style={{ width: '100%', maxWidth: 420 }}>
        <Col span={24}>
          <Flex vertical align="center" style={{ marginBottom: 28 }}>
            <Avatar
              size={56}
              icon={<FileProtectOutlined />}
              style={{
                background: 'var(--varexe-soft-primary)',
                color: 'var(--varexe-primary)',
                fontSize: 26,
                marginBottom: 12,
              }}
            />
            {orgInfoLoading && isOrgScopedRoute ? (
              <Spin size="small" style={{ marginBottom: 8 }} />
            ) : null}
            <Title
              level={4}
              style={{
                margin: 0,
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 700,
                color: 'var(--varexe-gray-800)',
                textAlign: 'center',
              }}
            >
              {heroTitle}
            </Title>
            <Text type="secondary" style={{ fontSize: 13, textAlign: 'center', display: 'block' }}>
              {heroSubtitle}
            </Text>
            {isOrgScopedRoute && orgName ? (
              <Text style={{ fontSize: 12, color: 'var(--varexe-gray-500)', marginTop: 8, textAlign: 'center', display: 'block' }}>
                Only use this page if you were sent this link by {orgName}.
              </Text>
            ) : null}
          </Flex>

          <Card
            style={{
            }}
            styles={{ body: { padding: '32px 36px' } }}
          >
            <Title level={4} style={{ margin: '0 0 4px', fontFamily: 'Poppins, sans-serif' }}>
              Sign in
            </Title>
            <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 24 }}>
              {cardIntro}
            </Text>

            {orgInfoError && (
              <Alert
                type="error"
                message={orgInfoError}
                showIcon
                style={{ marginBottom: 20, borderRadius: 6 }}
              />
            )}

            {error && (
              <Alert
                type="error"
                message={error}
                showIcon
                closable
                onClose={() => setError(null)}
                style={{ marginBottom: 20, borderRadius: 6 }}
              />
            )}

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              size="large"
              requiredMark={false}
            >
              {!isOrgScopedRoute && (
                <Form.Item
                  label="Organization ID"
                  name="organization_id"
                  rules={[{ required: true, message: 'Please enter your organization ID' }]}
                >
                  <Input
                    prefix={<BankOutlined style={{ color: 'var(--varexe-gray-400)' }} />}
                    placeholder="e.g. org_abc123"
                    autoComplete="organization"
                  />
                </Form.Item>
              )}

              <Form.Item
                label="Email address"
                name="email"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Enter a valid email address' },
                ]}
              >
                <Input
                  prefix={<MailOutlined style={{ color: 'var(--varexe-gray-400)' }} />}
                  placeholder="you@example.com"
                  autoComplete="email"
                />
              </Form.Item>

              <Form.Item
                label="Password"
                name="password"
                rules={[{ required: true, message: 'Please enter your password' }]}
                style={{ marginBottom: 24 }}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: 'var(--varexe-gray-400)' }} />}
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                block
                loading={loading}
                disabled={Boolean(isOrgScopedRoute && orgInfoError)}
                style={{ height: 42, fontWeight: 600, borderRadius: 6 }}
              >
                Sign in
              </Button>
            </Form>
          </Card>

          <Paragraph style={{ textAlign: 'center', marginTop: 20, color: 'var(--varexe-gray-500)', fontSize: 12 }}>
            Don&apos;t have access? Contact your account manager to get portal access.
          </Paragraph>

          <Paragraph style={{ textAlign: 'center', marginTop: 8, marginBottom: 0, fontSize: 11 }}>
            <PoweredByVarexe textColor="var(--varexe-gray-400)" fontSize={11} logoHeight={18} />
          </Paragraph>
        </Col>
      </Row>
    </Flex>
  );
};

export default LoginPage;
